import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  Divider,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchMyOrders } from '../features/ordersSlice';
import { formatCurrency } from '../utils/pricing';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { resolveImageUrl } from '../utils/image';
import { Product } from '../types';

const ORDER_STEPS = ['pending', 'processing', 'shipped', 'delivered'] as const;

const statusToStepIndex = (status: string): number => {
  const index = ORDER_STEPS.indexOf(status as (typeof ORDER_STEPS)[number]);
  return index >= 0 ? index : 0;
};

const formatStatusLabel = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1);

const AccountPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((state) => state.auth);
  const { myOrders, loading, error } = useAppSelector((state) => state.orders);
  const [reviewedProductIds, setReviewedProductIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (token) {
      dispatch(fetchMyOrders());
      apiClient
        .get(`${API_ENDPOINTS.REVIEWS}/mine`)
        .then((res) =>
          setReviewedProductIds(
            new Set(
              (res.data as Array<{ product: string }>).map((r) => String(r.product))
            )
          )
        )
        .catch(() => {});
    }
  }, [dispatch, token]);

  // Items from delivered orders the customer hasn't reviewed yet
  const reviewableProducts = useMemo(() => {
    const seen = new Map<string, Product>();
    myOrders
      .filter((order) => order.status === 'delivered')
      .forEach((order) =>
        order.items.forEach((item) => {
          const product = item.product;
          if (product?._id && !reviewedProductIds.has(product._id) && !seen.has(product._id)) {
            seen.set(product._id, product);
          }
        })
      );
    return Array.from(seen.values());
  }, [myOrders, reviewedProductIds]);

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        My Account
      </Typography>
      {user ? (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email} · {user.phone}
          </Typography>

          {reviewableProducts.length > 0 && (
            <Paper variant="outlined" sx={{ p: 3, mt: 3, backgroundColor: '#FCF9FB' }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Review your purchases
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Share your thoughts on pieces that have been delivered — it helps other shoppers.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {reviewableProducts.map((product) => (
                  <Box
                    key={product._id}
                    component={RouterLink}
                    to={`/products/${product.slug || product._id}?review=1`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      pr: 1.75,
                      border: '1px solid rgba(0,0,0,0.1)',
                      borderRadius: 2,
                      textDecoration: 'none',
                      color: 'inherit',
                      backgroundColor: '#fff',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <Box
                      component="img"
                      src={resolveImageUrl(product.images?.[0] || '')}
                      alt=""
                      sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" color="primary">
                        ★ Write a review
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              My Orders
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Track all your purchases and fulfillment status.
            </Typography>

            {loading && <Typography>Loading your orders...</Typography>}
            {!loading && error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {!loading && !error && myOrders.length === 0 && (
              <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  No orders yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your placed orders will appear here once you complete checkout.
                </Typography>
              </Paper>
            )}

            <Stack spacing={2}>
              {myOrders.map((order) => (
                <Paper key={order._id} variant="outlined" sx={{ p: 2.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 1,
                      flexDirection: { xs: 'column', sm: 'row' }
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Order #{order._id.slice(-8).toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Placed on {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={`Payment: ${formatStatusLabel(order.paymentStatus)}`}
                        color={order.paymentStatus === 'paid' ? 'success' : 'default'}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={formatStatusLabel(order.status)}
                        color={order.status === 'delivered' ? 'success' : order.status === 'cancelled' ? 'error' : 'primary'}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                      Order status timeline
                    </Typography>
                    {order.status === 'cancelled' ? (
                      <Alert severity="warning">This order has been cancelled.</Alert>
                    ) : (
                      <Stepper
                        activeStep={statusToStepIndex(order.status)}
                        alternativeLabel
                        sx={{ pt: 1 }}
                      >
                        {ORDER_STEPS.map((step) => (
                          <Step key={step}>
                            <StepLabel>{formatStatusLabel(step)}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    )}
                  </Box>

                  <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                    {order.items.map((item) => (
                      <Box
                        key={`${order._id}-${item.product._id}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {item.product.name} x{item.quantity}
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(item.price * item.quantity)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">Total</Typography>
                    <Typography variant="subtitle2">{formatCurrency(order.totalAmount)}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.5 }}>
                    <MuiButton
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/account/orders/${order._id}`}
                    >
                      View details
                    </MuiButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          You are not logged in.
        </Typography>
      )}
    </Box>
  );
};

export default AccountPage;

